import React from "react";
import Button from "@material-ui/core/Button";
import DialogActions from "@material-ui/core/DialogActions";
import moment from "moment";
import "moment/locale/pt-br";
import Moeda from "../../utils/Moeda";
import {
  Cliente,
  ClienteVenda,
  ClienteRecebimento,
  ClienteTrocaAvulso,
  CestaVenda,
  ProdutoVenda,
  ClienteAgendamento,
} from "../../repository/models";

const cliente = new Cliente();
const clienteVenda = new ClienteVenda();
const clienteRecebimento = new ClienteRecebimento();
const clienteTrocaAvulso = new ClienteTrocaAvulso();
const cestaVenda = new CestaVenda();
const produtoVenda = new ProdutoVenda();
const clienteAgendamento = new ClienteAgendamento();

export const MOVEMENT_TYPES = {
  VENDA: "Venda",
  RECEBIMENTO: "Recebimento",
  TROCA: "Troca",
};

export const DUPLICATE_RESOLUTION = {
  PROCEED: "proceed",
  REPLACE: "replace",
  DISCARD: "discard",
};

const DUPLICATE_WINDOW_MS = 2 * 60 * 1000;

function normalizeMoneyValue(value) {
  return Moeda.create(value).mount();
}

function parseMovementDate(date) {
  return moment(date).toDate();
}

function isWithinDuplicateWindow(movementDate, referenceDate) {
  const movementTime = parseMovementDate(movementDate).getTime();
  const referenceTime = parseMovementDate(referenceDate).getTime();
  return Math.abs(referenceTime - movementTime) <= DUPLICATE_WINDOW_MS;
}

function matchesExchangeValue(movement, saldoAdicionar, saldoRetirar) {
  return (
    normalizeMoneyValue(movement.saldoAdicionar) ===
      normalizeMoneyValue(saldoAdicionar) &&
    normalizeMoneyValue(movement.saldoRetirar) ===
      normalizeMoneyValue(saldoRetirar)
  );
}

async function findVendaDuplicate({ clienteId, valor, referenceDate }) {
  const normalizedValor = normalizeMoneyValue(valor);
  const movements = await clienteVenda.getAll("ClienteId", clienteId);

  return movements.find(
    (movement) =>
      !movement.cancelado &&
      isWithinDuplicateWindow(movement.dataRealizacao, referenceDate) &&
      normalizeMoneyValue(
        movement.valorFinalDescontado || movement.valor
      ) === normalizedValor
  );
}

async function findRecebimentoDuplicate({ clienteId, valor, referenceDate }) {
  const normalizedValor = normalizeMoneyValue(valor);
  const movements = await clienteRecebimento.getAll("ClienteId", clienteId);

  return movements.find(
    (movement) =>
      !movement.cancelado &&
      isWithinDuplicateWindow(movement.dataRealizacao, referenceDate) &&
      normalizeMoneyValue(movement.valor) === normalizedValor
  );
}

async function findTrocaDuplicate({
  clienteId,
  saldoAdicionar,
  saldoRetirar,
  referenceDate,
}) {
  const movements = await clienteTrocaAvulso.getAll("ClienteId", clienteId);

  return movements.find(
    (movement) =>
      !movement.cancelado &&
      movement.ativo !== false &&
      isWithinDuplicateWindow(movement.dataRealizacao, referenceDate) &&
      matchesExchangeValue(movement, saldoAdicionar, saldoRetirar)
  );
}

export async function findDuplicateMovement({
  clienteId,
  tipo,
  valor,
  saldoAdicionar,
  saldoRetirar,
  referenceDate = new Date(),
}) {
  if (!clienteId) return null;

  if (tipo === MOVEMENT_TYPES.VENDA) {
    return findVendaDuplicate({ clienteId, valor, referenceDate });
  }

  if (tipo === MOVEMENT_TYPES.RECEBIMENTO) {
    return findRecebimentoDuplicate({ clienteId, valor, referenceDate });
  }

  if (tipo === MOVEMENT_TYPES.TROCA) {
    return findTrocaDuplicate({
      clienteId,
      saldoAdicionar,
      saldoRetirar,
      referenceDate,
    });
  }

  return null;
}

function buildDuplicateMessage({
  tipo,
  duplicate,
  valor,
  clienteNome,
  saldoAdicionar,
  saldoRetirar,
}) {
  moment.locale("pt-br");
  const timeAgo = moment(duplicate.dataRealizacao).fromNow();

  if (tipo === MOVEMENT_TYPES.TROCA) {
    return (
      `Você já registrou uma troca com os mesmos valores para ${clienteNome} ` +
      `${timeAgo}. O que deseja fazer?`
    );
  }

  const formattedValor = Moeda.create(valor).format();

  return (
    `Você já tem uma ${tipo.toLowerCase()} de ${formattedValor} para ${clienteNome} ` +
    `feita ${timeAgo}. O que deseja fazer?`
  );
}

function promptDuplicateResolution(dialogContext, { message }) {
  return new Promise((resolve) => {
    const choose = (action) => {
      dialogContext.popDialog();
      resolve(action);
    };

    dialogContext.addDialog({
      type: "warning",
      title: "Possível movimentação duplicada",
      message,
      hasCloseButton: false,
      disableBackdropClick: true,
      dialogActions: React.createElement(
        DialogActions,
        {
          style: {
            flexDirection: "column",
            alignItems: "stretch",
            padding: 16,
          },
        },
        React.createElement(
          Button,
          {
            key: "proceed",
            onClick: () => choose(DUPLICATE_RESOLUTION.PROCEED),
            color: "primary",
            variant: "contained",
            style: { marginBottom: 8 },
          },
          "Manter as duas"
        ),
        React.createElement(
          Button,
          {
            key: "replace",
            onClick: () => choose(DUPLICATE_RESOLUTION.REPLACE),
            color: "primary",
            style: { marginBottom: 8 },
          },
          "Apagar a anterior e manter esta"
        ),
        React.createElement(
          Button,
          {
            key: "discard",
            onClick: () => choose(DUPLICATE_RESOLUTION.DISCARD),
            color: "default",
          },
          "Manter a anterior e descartar esta"
        )
      ),
    });
  });
}

function wasSyncedToServer(movement) {
  return movement.shouldSync === false;
}

async function deleteLocalRecord(model, record) {
  if (record?.DATABASE_ID != null) {
    await model.delete(record.DATABASE_ID);
  }
}

async function persistClientAfterDuplicateRemoval(client) {
  client.shouldSync = true;
  await cliente.put(client, false);
}

async function deleteVendaDuplicate(movement, client) {
  const linkedReceivements = await clienteRecebimento.getAll(
    "ClienteVendaId",
    movement.id
  );

  for (const receivement of linkedReceivements.filter((rec) => !rec.cancelado)) {
    client.saldoAtual = Moeda.create(client.saldoAtual)
      .subtract(receivement.valor)
      .mount();
    await deleteLocalRecord(clienteRecebimento, receivement);
  }

  const linkedExchanges = await clienteTrocaAvulso.getAll(
    "ClienteVendaId",
    movement.id
  );

  for (const exchange of linkedExchanges.filter(
    (item) => item.ativo !== false && !item.cancelado
  )) {
    await deleteLocalRecord(clienteTrocaAvulso, exchange);
  }

  const linkedSchedules = await clienteAgendamento.getAll(
    "ClienteVendaId",
    movement.id
  );

  for (const schedule of linkedSchedules) {
    await deleteLocalRecord(clienteAgendamento, schedule);
  }

  const linkedBaskets = await cestaVenda.getAll("ClienteVendaId", movement.id);
  for (const basket of linkedBaskets) {
    await deleteLocalRecord(cestaVenda, basket);
  }

  const linkedProducts = await produtoVenda.getAll("ClienteVendaId", movement.id);
  for (const product of linkedProducts) {
    await deleteLocalRecord(produtoVenda, product);
  }

  const saleValue = Moeda.create(
    movement.valorFinalDescontado || movement.valor
  ).mount();

  client.saldoAtual = Moeda.create(client.saldoAtual).add(saleValue).mount();
  await deleteLocalRecord(clienteVenda, movement);
  await persistClientAfterDuplicateRemoval(client);
}

async function deleteRecebimentoDuplicate(movement, client) {
  client.saldoAtual = Moeda.create(client.saldoAtual)
    .subtract(movement.valor)
    .mount();
  await deleteLocalRecord(clienteRecebimento, movement);
  await persistClientAfterDuplicateRemoval(client);
}

async function deleteTrocaDuplicate(movement) {
  await deleteLocalRecord(clienteTrocaAvulso, movement);
}

async function cancelVendaDuplicate(movement, client) {
  const linkedReceivements = await clienteRecebimento.getAll(
    "ClienteVendaId",
    movement.id
  );

  for (const receivement of linkedReceivements.filter((rec) => !rec.cancelado)) {
    client.saldoAtual = Moeda.create(client.saldoAtual)
      .subtract(receivement.valor)
      .mount();
    receivement.cancelado = true;
    receivement.shouldSync = true;
    await clienteRecebimento.put(receivement);
  }

  const linkedExchanges = await clienteTrocaAvulso.getAll(
    "ClienteVendaId",
    movement.id
  );

  for (const exchange of linkedExchanges.filter(
    (item) => item.ativo !== false && !item.cancelado
  )) {
    exchange.ativo = false;
    exchange.cancelado = true;
    exchange.shouldSync = true;
    await clienteTrocaAvulso.put(exchange);
  }

  const saleValue = Moeda.create(
    movement.valorFinalDescontado || movement.valor
  ).mount();

  client.saldoAtual = Moeda.create(client.saldoAtual).add(saleValue).mount();
  movement.cancelado = true;
  movement.shouldSync = true;
  await clienteVenda.put(movement);
  await persistClientAfterDuplicateRemoval(client);
}

async function cancelRecebimentoDuplicate(movement, client) {
  client.saldoAtual = Moeda.create(client.saldoAtual)
    .subtract(movement.valor)
    .mount();
  movement.cancelado = true;
  movement.shouldSync = true;
  await clienteRecebimento.put(movement);
  await persistClientAfterDuplicateRemoval(client);
}

async function cancelTrocaDuplicate(movement) {
  movement.ativo = false;
  movement.cancelado = true;
  movement.shouldSync = true;
  await clienteTrocaAvulso.put(movement);
}

async function removeVendaDuplicate(movement, client) {
  if (wasSyncedToServer(movement)) {
    await cancelVendaDuplicate(movement, client);
    return;
  }
  await deleteVendaDuplicate(movement, client);
}

async function removeRecebimentoDuplicate(movement, client) {
  if (wasSyncedToServer(movement)) {
    await cancelRecebimentoDuplicate(movement, client);
    return;
  }
  await deleteRecebimentoDuplicate(movement, client);
}

async function removeTrocaDuplicate(movement) {
  if (wasSyncedToServer(movement)) {
    await cancelTrocaDuplicate(movement);
    return;
  }
  await deleteTrocaDuplicate(movement);
}

export async function cancelDuplicateMovement({ duplicate, tipo, clienteId }) {
  const [client] = await cliente.getAll("id", [clienteId]);
  if (!client) {
    throw new Error("Cliente não encontrado.");
  }

  if (tipo === MOVEMENT_TYPES.VENDA) {
    await removeVendaDuplicate(duplicate, client);
    return;
  }

  if (tipo === MOVEMENT_TYPES.RECEBIMENTO) {
    await removeRecebimentoDuplicate(duplicate, client);
    return;
  }

  if (tipo === MOVEMENT_TYPES.TROCA) {
    await removeTrocaDuplicate(duplicate);
  }
}

export async function resolveDuplicateMovement({
  dialogContext,
  clienteId,
  clienteNome = "este cliente",
  tipo,
  valor,
  saldoAdicionar,
  saldoRetirar,
  referenceDate = new Date(),
}) {
  const duplicate = await findDuplicateMovement({
    clienteId,
    tipo,
    valor,
    saldoAdicionar,
    saldoRetirar,
    referenceDate,
  });

  if (!duplicate) {
    return { action: DUPLICATE_RESOLUTION.PROCEED, duplicate: null };
  }

  const message = buildDuplicateMessage({
    tipo,
    duplicate,
    valor,
    clienteNome,
    saldoAdicionar,
    saldoRetirar,
  });

  const action = await promptDuplicateResolution(dialogContext, { message });

  if (action === DUPLICATE_RESOLUTION.REPLACE) {
    await cancelDuplicateMovement({ duplicate, tipo, clienteId });
  }

  return { action, duplicate };
}

export function shouldProceedAfterDuplicateResolution(resolution) {
  return resolution?.action !== DUPLICATE_RESOLUTION.DISCARD;
}
